import type { InteractionDetail, InteractionSummary } from '../api/types';
import type { TimelineItem } from './useChat';
import type { UserAttachment } from './useChatStore';

export interface ChatHistoryTimelineClient {
  listInteractions: (
    sessionId: string,
    limit?: number,
    offset?: number,
  ) => Promise<{
    items: InteractionSummary[];
    has_more: boolean;
  }>;
  getInteraction: (interactionId: string) => Promise<InteractionDetail>;
}

export function toTimeline(
  items: Array<Record<string, unknown>>,
  interactionId: string,
): TimelineItem[] {
  const out: TimelineItem[] = [];
  let blockNum = 0;

  for (const turn of items) {
    const content = (turn.content as Array<Record<string, unknown>> | undefined) ?? [];
    for (const piece of content) {
      if (piece.type === 'text') {
        const text = String(piece.text ?? '').trim();
        if (!text) continue;
        out.push({
          kind: 'agent',
          id: `hist-agent-${interactionId}-${out.length}`,
          text,
        });
      }
      if (piece.type === 'code') {
        const execId = `hist-exec-${interactionId}-${blockNum++}`;
        out.push({
          kind: 'code',
          id: execId,
          execId,
          code: String(piece.code ?? ''),
          output: String(piece.stdout ?? piece.error ?? ''),
          outputType: 'text',
          metadata: {},
          status: piece.error ? 'error' : 'success',
        });
      }
      if (piece.type === 'user_download') {
        const downloadId = String(piece.download_id ?? '').trim();
        if (!downloadId) continue;
        out.push({
          kind: 'download',
          id: `hist-download-${interactionId}-${downloadId}`,
          downloadId,
          filename: String(piece.filename ?? 'download'),
          size: Number(piece.size ?? 0),
          label: piece.label ? String(piece.label) : undefined,
        });
      }
      if (piece.type === 'auth_request') {
        out.push({
          kind: 'auth_request',
          id: `hist-auth-${interactionId}-${String(piece.provider ?? 'google')}`,
          provider: String(piece.provider ?? 'google'),
          displayName: String(piece.display_name ?? piece.provider ?? 'google'),
          iconUrl: String(piece.icon_url ?? ''),
          authStartUrl: '',
          status: (piece.status === 'success'
            ? 'success'
            : piece.status === 'error'
              ? 'error'
              : 'pending') as 'pending' | 'success' | 'error',
        });
      }
    }
  }

  return out;
}

export async function loadFullSessionTimeline(
  client: ChatHistoryTimelineClient,
  sessionId: string,
): Promise<TimelineItem[]> {
  const pageSize = 200;
  const detailBatchSize = 10;
  const interactions: InteractionSummary[] = [];
  let offset = 0;

  while (true) {
    const page = await client.listInteractions(sessionId, pageSize, offset);
    interactions.push(...page.items);
    if (!page.has_more) break;
    offset += page.items.length;
    if (page.items.length === 0) break;
  }

  const completedInteractions = interactions.filter((interaction) => interaction.completed_at);
  const out: TimelineItem[] = [];

  for (let i = 0; i < completedInteractions.length; i += detailBatchSize) {
    const batch = completedInteractions.slice(i, i + detailBatchSize);
    const settled = await Promise.allSettled(
      batch.map(async (interaction) => {
        let lastErr: unknown;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            return { interaction, full: await client.getInteraction(interaction.id) };
          } catch (err) {
            lastErr = err;
          }
        }
        throw lastErr;
      }),
    );

    for (const entry of settled) {
      if (entry.status !== 'fulfilled') continue;
      const { interaction, full } = entry.value;
      const filePaths = interaction.file_paths ?? full.file_paths ?? [];
      const attachments: UserAttachment[] = filePaths.map((p) => ({
        filename: p.split('/').pop() || p,
        path: p,
      }));
      if (interaction.source === 'self') {
        out.push({
          kind: 'system',
          id: `hist-user-${interaction.id}`,
          text: 'Agent is working on a self-initiated task…',
        });
      } else {
        const userItem: TimelineItem = {
          kind: 'user',
          id: `hist-user-${interaction.id}`,
          text: interaction.prompt,
        };
        if (attachments.length > 0) {
          userItem.attachments = attachments;
        }
        out.push(userItem);
      }

      const turnItems = toTimeline(full.turns as Array<Record<string, unknown>>, interaction.id);
      out.push(...turnItems);
    }
  }

  return out;
}
